// UserContext.js
import React, {createContext, useContext, useEffect, useState} from "react";
import axios from "axios";
import {axiosInstance} from "../axios";

export const UserContext = createContext();

export const UserProvider = ({children}) => {
    const [version, setVersion] = useState(0);

    const signOutOuter = (redirect) => {
        try {
            localStorage.clear();
            delete axios.defaults.headers.common.Authorization;
            delete axiosInstance.defaults.headers.common.Authorization;
        } finally {
            // remount vnitřního provideru => kompletní reset stavů
            setVersion(v => v + 1);
            redirect?.();
        }
    };

    return (
        <InnerUserProvider key={version} signOutOuter={signOutOuter}>
            {children}
        </InnerUserProvider>
    );
};

const InnerUserProvider = ({children, signOutOuter}) => {
    const [user, setUser] = useState();
    const [token, setToken] = useState();
    const [testResultList, setTestResultList] = useState();
    const [courseRegistration, setCourseRegistration] = useState();
    const [error, setError] = useState();
    const [hasSeenTestIntroduction, setHasSeenTestIntroduction] = useState(false);
    const [testIntroductionEndTime, setTestIntroductionEndTime] = useState();
    const [testToComplete, setTestToComplete] = useState();
    const [otherTest, setOtherTest] = useState();
    const [skipSortingTest, setSkipSortingTest] = useState();
    const [isSortingTestFinished, setIsSortingTestFinished] = useState(false);
    const [testQuestionResult, setTestQuestionResult] = useState();

    const handleUserData = (data) => {
        setUser(data.user);
        setTestResultList(data.testResultList);
        setCourseRegistration(data.courseRegistration);
        setIsSortingTestFinished(data.isSortingTestFinished);
        setSkipSortingTest(data.isEligibleForSkipSortingTest);

        if (data.user.testIntroductionStartTime) {
            setHasSeenTestIntroduction(true);
            const secs = parseInt(process.env.REACT_APP_TEST_INTRODUCTION_TIME);
            setTestIntroductionEndTime(new Date(data.user.testIntroductionStartTime).getTime() + secs * 1000);
        }
        if (data.sortingTestToComplete) {
            setTestToComplete({testType: "sortingTest", testIndex: data.sortingTestToComplete});
        }
        const testData = localStorage.getItem("otherTestData");
        if (testData) setOtherTest(JSON.parse(testData));
    };

    useEffect(() => {
        const checkTokenValidity = async () => {
            const accessToken = localStorage.getItem("accessToken");
            if (!accessToken) return;
            try {
                const {data} = await axios.post("/api/user/loadAll", {}, {headers: {Authorization: `Bearer ${accessToken}`}});
                setToken(accessToken);
                handleUserData(data);
            } catch (e) {
                setError(e);
                signOutOuter(); // při neplatném tokenu taky remount
            }
        };
        checkTokenValidity();
    }, [signOutOuter]);

    const signIn = async (email, password, stayLoggedIn = false) => {
        const {data} = await axios.post("/api/login", {email, password, stayLoggedIn});
        localStorage.setItem("accessToken", data.accessToken);
        setToken(data.accessToken);
        if (stayLoggedIn && data.refreshToken) {
            localStorage.setItem("refreshToken", data.refreshToken);
        }
        handleUserData(data);
    };

    const signUp = async (formData, stayLoggedIn = false) => {
        const {data} = await axios.post("/api/user/register", {...formData, stayLoggedIn, sendResetPasswordEmail: false});
        localStorage.setItem("accessToken", data.accessToken);
        setToken(data.accessToken);
        handleUserData(data);
    };

    const signOut = (redirect) => {
        signOutOuter(redirect); // jen delegace – reset zajistí Outer
    };

    const renewToken = async () => {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) return;
        const {data} = await axios.post("/api/refresh-token", {refreshToken});
        localStorage.setItem("accessToken", data.accessToken);
        setToken(data.accessToken);
        return data.accessToken;
    };

    const startOtherTest = (testId, testToComplete) => {
        const otherTestData = {testId, testToComplete};
        localStorage.setItem("otherTestData", JSON.stringify(otherTestData));
        setOtherTest(otherTestData);
    };
    const endOtherTest = () => {
        localStorage.removeItem("otherTestData");
        setOtherTest(null);
    };

    const recordTestIntroductionStart = async () => {
        const currentTime = new Date();
        try {
            await axios.post("/api/user/recordTestIntroductionStart", {startTime: currentTime}, {headers: {Authorization: `Bearer ${token}`}});
            setUser(prev => ({...prev, testIntroductionStartTime: currentTime}));
            setHasSeenTestIntroduction(true);
            const secs = parseInt(process.env.REACT_APP_TEST_INTRODUCTION_TIME);
            setTestIntroductionEndTime(currentTime.getTime() + secs * 1000);
        } catch (error) {
            setError(error);
        }
    };

    const addTestResult = (testResult) => {
        setTestResultList(prev => [...(prev || []), testResult]);
    };

    const handleTestStart = async (period, testType, testIndex, testId) => {
        if (!testQuestionResult) {
            const testData = {testType, testIndex, testId};
            try {
                const {data} = await axiosInstance.post(`/api/testQuestionsResult/${period}/start`, testData);
                setTestQuestionResult(data);
                return data.answerMap;
            } catch (error) {
                setError(error);
            }
        }
        return testQuestionResult?.answerMap;
    };

    const handleTestEnd = async () => {
        if (testQuestionResult) {
            try {
                await axiosInstance.post(`/api/testQuestionsResult/${testQuestionResult._id}/end`);
                setTestQuestionResult(null);
            } catch (error) {
                setError(error);
            }
        }
    };

    const handleTestUpdate = async (answerMap) => {
        if (!testQuestionResult) return;
        try {
            const {data} = await axiosInstance.put(`/api/testQuestionsResult/${testQuestionResult._id}`, answerMap);
            setTestQuestionResult(data);
        } catch (error) {
            setError(error);
        }
    };

    return (
        <UserContext.Provider
            value={{
                user,
                signIn,
                signUp,
                signOut,            // <- používáš dál jako dřív
                renewToken,
                token,
                error,
                testResultList,
                courseRegistration,
                setCourseRegistration,
                addTestResult,
                hasSeenTestIntroduction,
                testIntroductionEndTime,
                recordTestIntroductionStart,
                testToComplete,
                startOtherTest,
                otherTest,
                endOtherTest,
                skipSortingTest,
                setTestToComplete,
                isSortingTestFinished,
                setIsSortingTestFinished,
                handleTestStart,
                handleTestEnd,
                handleTestUpdate
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useUserData = () => useContext(UserContext);
